import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Wide cinematic showcase matching the homepage hero visual system:
 * black background, radial neon glow, one dominant collectible image,
 * small category label, strong headline, <=2 sentence copy, one primary
 * action + optional secondary link. Alternates image side via `reverse`.
 * On mobile the image always stacks above the copy and stays fully visible.
 */
export const CinematicSection = ({
  image,
  alt = "",
  label,
  headline,
  accent,
  sentence,
  primaryTo = "/submit",
  primaryLabel = "SUBMIT YOUR CARDS",
  secondaryTo = "/results",
  secondaryLabel = "View Results",
  reverse = false,
  contain = true,
}) => {
  return (
    <section className="relative overflow-hidden bg-ink border-b border-[color:var(--line)]">
      <div className="absolute inset-0 pointer-events-none opacity-70"
        style={{ background: reverse
          ? "radial-gradient(55% 70% at 78% 40%, rgba(59,123,255,0.20), rgba(155,92,255,0.10) 55%, transparent 75%)"
          : "radial-gradient(55% 70% at 22% 40%, rgba(59,123,255,0.20), rgba(155,92,255,0.10) 55%, transparent 75%)" }} />
      <div className={`relative mx-auto max-w-[1300px] px-5 py-16 sm:py-20 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? "lg:[direction:rtl]" : ""}`}>
        {/* Dominant image */}
        <div className="[direction:ltr] order-1 lg:order-none relative">
          <div className="absolute -inset-6 rounded-[2rem] pointer-events-none opacity-70"
            style={{ background: "radial-gradient(60% 60% at 50% 40%, rgba(36,224,255,0.22), rgba(155,92,255,0.12) 55%, transparent 75%)" }} />
          <img
            src={image}
            alt={alt}
            className={`relative w-full rounded-2xl ${contain ? "object-contain max-h-[440px] sm:max-h-[520px]" : "object-cover aspect-[4/3]"} ${contain ? "" : "border border-[color:var(--line)]"} drop-shadow-[0_20px_60px_rgba(0,0,0,0.6)]`}
          />
        </div>

        {/* Copy */}
        <div className="[direction:ltr] order-2 lg:order-none text-center lg:text-left">
          {label && <p className="eyebrow text-[color:var(--cyan)] mb-4">{label}</p>}
          <h2 className="h-display text-[28px] leading-[1.04] sm:text-4xl lg:text-5xl text-chrome">
            {headline}
            {accent && <span className="block text-holo mt-1">{accent}</span>}
          </h2>
          {sentence && (
            <p className="text-sm sm:text-base text-[color:var(--chrome-2)] mt-5 max-w-md mx-auto lg:mx-0 leading-relaxed">
              {sentence}
            </p>
          )}
          <div className="flex flex-col sm:flex-row items-center gap-4 mt-8 justify-center lg:justify-start">
            <Link to={primaryTo} className="btn-holo rounded-xl px-8 py-4 text-sm inline-flex items-center gap-2">
              {primaryLabel} <ArrowRight size={16} />
            </Link>
            {secondaryTo && (
              <Link to={secondaryTo}
                className="text-xs font-tech tracking-widest text-[color:var(--chrome-3)] hover:text-[color:var(--cyan)] transition-colors inline-flex items-center gap-2">
                {secondaryLabel} <ArrowRight size={13} />
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
